import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { Document, DocumentRequest } from '../lib/tenant-api';
import Modal from './Modal';
import clsx from 'clsx';

interface EditDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (documentData: DocumentRequest) => Promise<void>;
  document: Document;
}

const SOURCE_LABELS: Record<Document['source'], string> = {
  manual: 'Manual',
  upload: 'Upload',
  cms: 'CMS',
  api: 'API',
  integration: 'Integration',
};

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  document,
}) => {
  const [formData, setFormData] = useState<DocumentRequest>({
    title: '',
    content: '',
    document_type: 'faq',
    source: 'manual',
    category: '',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title,
        content: document.content,
        document_type: document.document_type,
        source: document.source,
        category: document.category || '',
        tags: document.tags || [],
      });
      setTagInput('');
      setShowTagInput((document.tags || []).length > 0);
    }
  }, [document]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save document';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const addTag = (value?: string) => {
    const tag = (value || tagInput).trim();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }));
    }
    setTagInput('');
    setShowTagInput(true);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => {
      const nextTags = prev.tags?.filter((tag) => tag !== tagToRemove) || [];
      if (nextTags.length === 0) {
        setShowTagInput(false);
      }
      return { ...prev, tags: nextTags };
    });
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={loading}
        className="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="edit-document-form"
        disabled={loading}
        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {loading ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  );

  const hasTags = (formData.tags?.length ?? 0) > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit document"
      size="xl"
      footer={footer}
      contentClassName="flex-1 overflow-y-auto px-4 sm:px-6"
    >
      <form id="edit-document-form" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-0 lg:grid-cols-2 lg:divide-x lg:divide-gray-200">
          <div className="space-y-5 py-4 lg:pr-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="source" className="block text-sm font-medium text-gray-700">
                Source
              </label>
              <input
                type="text"
                id="source"
                value={SOURCE_LABELS[document.source] || document.source}
                readOnly
                className="mt-1 block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
              />
            </div>

            <div>
              <label htmlFor="document_type" className="block text-sm font-medium text-gray-700">
                Type
              </label>
              <select
                id="document_type"
                value={formData.document_type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    document_type: e.target.value as DocumentRequest['document_type'],
                  }))
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
                disabled={loading}
              >
                <option value="faq">FAQ</option>
                <option value="policy">Policy</option>
                <option value="guide">Guide</option>
                <option value="manual">Manual</option>
                <option value="article">Article</option>
                <option value="other">Others</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tag</label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {formData.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-indigo-500 hover:text-indigo-700"
                      disabled={loading}
                      aria-label={`Remove tag ${tag}`}
                    >
                      <XIcon className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                {showTagInput || hasTags ? (
                  <input
                    ref={tagInputRef}
                    id="tag-input"
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={() => {
                      if (tagInput.trim()) {
                        addTag();
                      } else if (!hasTags) {
                        setShowTagInput(false);
                      }
                    }}
                    placeholder="Enter tag name"
                    className="w-36 rounded border border-indigo-400 px-2 py-0.5 text-xs focus:border-indigo-400 focus:outline-none focus:ring-0"
                    disabled={loading}
                    autoFocus={showTagInput && !hasTags}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowTagInput(true);
                      setTagInput('');
                      requestAnimationFrame(() => tagInputRef.current?.focus());
                    }}
                    className="inline-flex items-center rounded border border-dashed border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    disabled={loading}
                  >
                    <PlusIcon className="mr-0.5 h-3 w-3" />
                    Add Tag
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-gray-50 lg:mt-0">
            <div className="py-4 px-4 lg:pl-6">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Content
              </label>
              <textarea
                id="content"
                rows={16}
                value={formData.content}
                onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                className={clsx(
                  'mt-1 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm',
                  'focus:border-gray-200 focus:outline-none focus:ring-0',
                  'min-h-[320px] lg:min-h-[420px]'
                )}
                required
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="pb-4">
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditDocumentModal;
